import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  VideoCameraIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import SlideContentModal from './SlideContentModal';
import SlideCard from './SlideCard';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { ContentItem } from '../types/content';

interface ContentSection {
  id: string;
  title: string;
  items: ContentItem[];
}

export default function HeroSliderContentContainer() {
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ sectionId: string; item: ContentItem } | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);

      const { data: existingSection, error: checkError } = await supabase
        .from('content_sections')
        .select('id')
        .eq('page', 'home')
        .eq('title', 'Hero Slider')
        .maybeSingle();

      if (checkError) throw checkError;

      if (!existingSection) {
        const { error: insertError } = await supabase
          .from('content_sections')
          .insert([{
            page: 'home',
            title: 'Hero Slider',
            icon: 'VideoCameraIcon'
          }]);
        if (insertError) throw insertError;
      }

      const { data: dbSections, error } = await supabase
        .from('content_sections')
        .select(`*, content_items (*)`)
        .eq('page', 'home')
        .eq('title', 'Hero Slider');

      if (error) throw error;

      if (dbSections) {
        const processedSections = dbSections.map(dbSection => ({
          id: dbSection.id,
          title: dbSection.title,
          items: (dbSection.content_items || []).map((item: any) => ({
            ...item,
            lastUpdated: new Date(item.updated_at || Date.now()).toISOString().split('T')[0]
          }))
        }));
        setSections(processedSections);

        if (!expandedSection && processedSections.length > 0 && processedSections[0]) {
          setExpandedSection(processedSections[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      toast.error('Failed to load slides');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handleEditClick = (sectionId: string, item: ContentItem) => {
    const editItem = {
      ...item,
      content: typeof item.content === 'string' ? item.content : JSON.stringify(item.content)
    };
    setSelectedItem(editItem);
    setSelectedSection(sectionId);
    setIsModalOpen(true);
  };

  const handleAddClick = (sectionId: string) => {
    setSelectedItem(null);
    setSelectedSection(sectionId);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (sectionId: string, item: ContentItem) => {
    setItemToDelete({ sectionId, item });
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (item: ContentItem) => {
    try {
      let parsedContent: any;
      try {
        parsedContent = JSON.parse(item.content);
      } catch {
        parsedContent = item.content;
      }

      const itemData = {
        title: item.title,
        type: item.type,
        content: parsedContent,
        status: item.status,
        section_id: selectedSection
      };

      if (selectedItem?.id) {
        const { error } = await supabase
          .from('content_items')
          .update(itemData)
          .eq('id', selectedItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('content_items')
          .insert([itemData]);
        if (error) throw error;
      }

      await fetchContent();
      setIsModalOpen(false);
      toast.success('Slide saved successfully');
    } catch (err: any) {
      console.error('Error saving slide:', err);
      toast.error(err.message || 'Failed to save slide');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete?.item.id) return;

    try {
      const { error } = await supabase
        .from('content_items')
        .delete()
        .eq('id', itemToDelete.item.id);
      if (error) throw error;

      await fetchContent();
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success('Slide deleted successfully');
    } catch (err) {
      console.error('Error deleting slide:', err);
      toast.error('Failed to delete slide');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <motion.div
          key={section.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div
            className="flex items-center justify-between p-6 cursor-pointer"
            onClick={() => toggleSection(section.id)}
          >
            <div className="flex items-center space-x-4">
              <div className="bg-golf-orange/10 p-3 rounded-xl">
                <VideoCameraIcon className="w-6 h-6 text-golf-orange" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golf-gray">{section.title}</h3>
                <p className="text-sm text-gray-500">{section.items.length} slides</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddClick(section.id);
                }}
                className="p-2 text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
              {expandedSection === section.id ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {expandedSection === section.id && (
            <div className="border-t border-gray-200 p-6">
              {section.items.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {section.items.map((item) => (
                    <SlideCard
                      key={item.id}
                      item={item}
                      onEdit={() => handleEditClick(section.id, item)}
                      onDelete={() => handleDeleteClick(section.id, item)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <VideoCameraIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No slides yet. Click the + button above to add one.</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      ))}

      <SlideContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        item={selectedItem}
        sectionId={selectedSection}
        sections={sections}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={itemToDelete?.item.title || ''}
      />
    </div>
  );
}

